<template>
	<ThePrintLayout>

		<div v-if="loading" class="flex justify-center items-center min-h-[29.7cm]">
			<span class="loading loading-spinner loading-lg"></span>
		</div>

		<div v-else-if="error" class="flex justify-center items-center min-h-[29.7cm]">
			<div class="alert alert-error max-w-md">{{ error }}</div>
		</div>

		<template v-else-if="pupil">

			<!-- Header -->
			<div class="flex justify-between items-center pb-4 mb-4 border-b-2">
				<div class="flex items-center gap-4 border rounded-xl p-4">
					<PupilSymbol :pupil="pupil" class="w-14 shrink-0" />
					<div class="text-xl font-bold leading-tight">
						<div>{{ pupil.firstName }}</div>
						<div>{{ pupil.lastName }}</div>
					</div>
				</div>

				<div class="text-right">
					<div class="font-semibold">{{ organisation?.name }}</div>
					<div>{{ $t('School year') }} {{ schoolYearLabel }}</div>
				</div>
			</div>

			<!-- Legend -->
			<div class="flex flex-col items-end gap-1 mb-8">
				<div v-for="level in evaluationLevels" :key="level.id" class="flex items-center gap-2">
					<span class="text-sm">{{ levelDisplayName(level) }}</span>
					<span class="w-4 h-4 rounded-full shrink-0" :style="{ backgroundColor: level.color }"></span>
				</div>
			</div>

			<!-- Goal groups -->
			<template v-for="structure in goalStructures" :key="structure.sourceId ?? structure.name">
				<div
					v-for="group in structure.items"
					:key="group.path"
					class="break-inside-avoid-page"
				>
					<!-- Group header -->
					<div class="flex items-center gap-3 pt-4 pb-2 break-after-avoid">
						<span
							class="text-white text-xs font-bold px-2 py-1 rounded shrink-0"
							:style="{ backgroundColor: group.color || '#818cf8' }"
						>{{ group.path }}</span>
						<span class="font-bold uppercase text-sm tracking-wide">{{ group.title }}</span>
					</div>

					<!-- Goals -->
					<div
						v-for="goalSearchResult in group.goalsFlat"
						:key="goalSearchResult.goal.id || goalSearchResult.goal.fullPath"
						class="flex items-start gap-4 py-2 border-t break-inside-avoid"
					>
						<span class="text-xs text-gray-400 min-w-16 shrink-0 pt-0.5 font-mono">{{ goalSearchResult.goal.fullPath }}</span>
						<span class="flex-1 text-sm leading-snug">
							<div>
								{{ goalSearchResult.goal.customTitle || goalSearchResult.goal.title }}
							</div>
							<div v-if="showNotes && getEvaluationForGoal(goalSearchResult.goal)?.comment" class="text-xs ml-6 text-base-content-light mt-2">
								<div>{{ getEvaluationForGoal(goalSearchResult.goal).author?.name }} - {{ formattedEvaluationDate(getEvaluationForGoal(goalSearchResult.goal).date) }}</div>
								“<span class="italic">{{ getEvaluationForGoal(goalSearchResult.goal).comment }}</span>”
							</div>
						</span>
						<div class="shrink-0 w-6 flex justify-center pt-0.5">
							<span
								v-if="goalSearchResult.goal.hasEvaluation(pupil)"
								class="w-5 h-5 rounded-full block"
								:style="{ backgroundColor: goalSearchResult.goal.getEvaluation(pupil)?.evaluationLevel?.color }"
							></span>
							<span v-else class="w-5 h-5 rounded-full block bg-gray-100"></span>
						</div>
					</div>
				</div>
			</template>

			<div v-if="!goalStructures.some((s) => s.items.length)" class="text-center text-gray-400 py-16 text-sm">
				{{ $t('No goals found') }}
			</div>

		</template>

	</ThePrintLayout>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { useForkedGoalsStore } from "@/stores/Goals.store.ts";
import { usePupilsStore } from "@/stores/Pupils.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { useEvaluationLevelsStore } from "@/stores/EvaluationLevels.store";
import { GOAL_SCOPES, GoalFilterDef, LESSON_TYPES } from "@/stores/filters/GoalFilterDef";
import { Pupil } from "@/models/Pupil.model";
import { EvaluationLevel } from "@/models/EvaluationLevel.model";
import { PopulatedGoalStructureContent } from "@/utils/GoalSource";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import ThePrintLayout from "@/components/layout/ThePrintLayout.vue";
import { Goal } from "@/models/Goal.model";
import { DateTime, Interval } from "luxon";

export default {
	name: 'PupilReport',

	components: {
		ThePrintLayout,
		PupilSymbol,
	},

	data() {
		return {
			loading: true,
			error: null as string | null,
			pupil: null as Pupil | null,
			showNotes: false,
			// Date range to report on, taken from the `since`/`until` query parameters.
			// Null when they are absent or unparsable; the organisation's current school year is used then.
			requestedDateRange: null as Interval | null,
		};
	},

	computed: {
		...mapStores(
			usePupilsStore,
			useOrganisationsStore,
			useEvaluationLevelsStore,
			useForkedGoalsStore('pupilReport')
		),

		goalStructures(): PopulatedGoalStructureContent[] {
			return this.pupilReportStore.structuredGoals ?? [];
		},

		organisation() {
			return this.organisationsStore.currentOrganisation;
		},

		/**
		 * The period the report is about: the range that was selected in the goal overview the
		 * report was opened from, falling back to the organisation's current school year.
		 */
		dateRange(): Interval {
			if (this.requestedDateRange) {
				return this.requestedDateRange;
			}

			return this.organisationsStore.getCurrentOrganisation().getCurrentSchoolYearInterval();
		},

		/**
		 * Whether the report covers the running school year. A report on a previous school
		 * year is not scoped to the school's current curriculum: those evaluations were made
		 * against whatever curriculum the school used back then.
		 */
		reportsCurrentSchoolYear(): boolean {
			return this.dateRange.contains(DateTime.now());
		},

		schoolYearLabel(): string {
			const range = this.dateRange;
			if (!range.isValid) return '';
			return `${range.start.year} - ${range.end.year}`;
		},

		evaluationLevels(): EvaluationLevel[] {
			return this.evaluationLevelsStore.items as EvaluationLevel[];
		},

		/**
		 * Defines what goals should be shown in the report.
		 */
		filter(): GoalFilterDef {

			if (!this.pupil) {
				throw new Error('Pupil not loaded');
			}

			const org = this.organisationsStore.getCurrentOrganisation();

			const filter = new GoalFilterDef();
			filter.goalScopes = [ GOAL_SCOPES.EVALUATED ];
			filter.lessonTypes = [
				LESSON_TYPES.COLLECTIVE,
				LESSON_TYPES.ACTIVITIES,
				LESSON_TYPES.THEMES
			];
			filter.pupilIds = [ this.pupil.id ];

			const classroomIds = this.pupil.classroomRelations
				.filter((r) => r.active)
				.map((r) => r.classroom.getId());

			if (classroomIds.length > 0) {
				filter.classroomIds = classroomIds;
			} else {
				filter.classroomIds = org.classrooms.map((c) => c.getId());
			}

			const dateRange = this.dateRange;
			if (dateRange.isValid) {
				filter.start = dateRange.start.toJSDate();
				filter.end = dateRange.end.toJSDate();
			}

			return filter;
		},
		
		locale() {
			return this.$i18n.locale;
		},
	},

	async created() {
		const pupilId = this.$route.params.pupilId as string;
		this.showNotes = this.$route.query.notes === 'true';

		// The period to report on, as ISO dates (see PupilGoalEvaluation.openReport).
		const since = DateTime.fromISO(this.$route.query.since as string ?? '');
		const until = DateTime.fromISO(this.$route.query.until as string ?? '');
		if (since.isValid && until.isValid && until >= since) {
			this.requestedDateRange = Interval.fromDateTimes(since.startOf('day'), until.endOf('day'));
		}

		try {
			await this.evaluationLevelsStore.loadOnce();
			// The pupil list is paginated, so the pupil may not be in the store: fetch it directly.
			this.pupil = this.pupilsStore.find(pupilId) ?? await this.loadPupil(pupilId);
			if (!this.pupil) {
				this.error = this.$t('Pupil not found');
				this.loading = false;
				return;
			}

			document.title = `${this.pupil.getFullName()} - Smart Symbols`;

			const org = this.organisationsStore.getCurrentOrganisation();
			if (!org.curriculum) {
				this.error = this.$t('No curriculum configured');
				this.loading = false;
				return;
			}

			// Only the current school year is scoped to the school's curriculum (see reportsCurrentSchoolYear).
			this.pupilReportStore.setCurriculum(this.reportsCurrentSchoolYear ? org.curriculum : null);
			await this.pupilReportStore.applyFilter(this.filter, true);

		} catch (e: any) {
			this.error = e.message || this.$t('An error occurred');
		} finally {
			this.loading = false;
		}
	},

	methods: {
		/**
		 * Fetch a single pupil from the API; resolves to null when the pupil does not exist.
		 */
		async loadPupil(pupilId: string): Promise<Pupil | null> {
			try {
				return await this.pupilsStore.loadById(pupilId);
			} catch (e: any) {
				if (e?.response?.status === 404) {
					return null;
				}
				throw e;
			}
		},

		levelDisplayName(level: EvaluationLevel): string {
			if (level.nameTranslationKey) {
				return this.$t(level.nameTranslationKey);
			}
			return level.name;
		},

		getEvaluationForGoal(goal: Goal) {
			return this.pupil ? goal.getEvaluation(this.pupil) : null;
		},

		formattedEvaluationDate(date: Date): string {
            if (!date) return '';
            const res = new Date(date);
            return res.toLocaleDateString(this.locale);
        }
	},
};
</script>
